import React, { useState } from 'react';

import { Box, Checkbox, Collapse, FormControlLabel } from '@mui/material';

import {
  PRIVACY_CHECKBOX_ID,
  PRIVACY_ERROR_ID,
  PRIVACY_FIELD_NAME,
  PRIVACY_HEADING_ID,
  PRIVACY_SECTION_ID
} from './constants';
import { SectionHeading } from './SectionHeading';
import { classes } from './styles';

interface PrivacyAcknowledgementProps {
  agreeLabel: string;
  descriptionHtml: string;
  headingText: string;
  initialChecked?: boolean;
  error: string;
  onChange?: (checked: boolean) => void;
  fontFamily?: string;
}

// `descriptionHtml` is sanitized server-side via Rails `sanitize` with a
// tag/attribute allowlist (see Jobs::WcagApplicationFlowHelper#wcag_privacy_flow_props).
// TinyMCE already produces correct HTML semantics: Enter -> new <p> with
// paragraph spacing via classes.privacy.descriptionSx; Shift+Enter -> <br>
// inside the same <p>, rendered as a soft line break.
export const PrivacyAcknowledgement: React.FC<PrivacyAcknowledgementProps> = ({
  agreeLabel,
  descriptionHtml,
  headingText,
  initialChecked = false,
  error,
  onChange,
  fontFamily
}) => {
  const [checked, setChecked] = useState<boolean>(initialChecked);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setChecked(isChecked);
    onChange?.(isChecked);
  };

  return (
    <Box
      aria-labelledby={PRIVACY_HEADING_ID}
      component="section"
      id={PRIVACY_SECTION_ID}
      sx={classes.privacy.containerSx()}
    >
      <SectionHeading fontFamily={fontFamily} id={PRIVACY_HEADING_ID}>
        {headingText}
      </SectionHeading>

      <Box
        className="wcag-rich-text"
        dangerouslySetInnerHTML={{ __html: descriptionHtml }}
        sx={classes.privacy.descriptionSx()}
      />

      {/* MUI Checkbox submits no value when unchecked; this hidden sibling
          preserves explicit unchecked semantics for save-for-later. */}
      <input name={PRIVACY_FIELD_NAME} type="hidden" value="0" />
      <FormControlLabel
        sx={classes.privacy.checkboxRowSx()}
        control={
          <Checkbox
            checked={checked}
            name={PRIVACY_FIELD_NAME}
            onChange={handleChange}
            sx={classes.privacy.checkboxSx()}
            value="1"
            inputProps={{
              id: PRIVACY_CHECKBOX_ID,
              'aria-required': true,
              'aria-describedby': error ? PRIVACY_ERROR_ID : undefined,
              'aria-invalid': !!error
            }}
          />
        }
        label={
          <>
            {agreeLabel}
            <span aria-label="required" style={classes.shared.requiredIndicatorStyle}>
              {' '}
              *
            </span>
          </>
        }
      />

      <Collapse unmountOnExit in={!!error}>
        <Box
          data-validation-error
          id={PRIVACY_ERROR_ID}
          role="alert"
          sx={classes.privacy.errorSx()}
        >
          {error}
        </Box>
      </Collapse>
    </Box>
  );
};
